// hooks/useFullscreen.js
import { ref, nextTick } from "vue";

/**
 * useFullscreen
 * 提供全屏切换和页面自适应缩放的组合式函数。
 * @param {string} selector - 需要全屏的元素选择器，默认 ".templateBigScreen2"
 * @param {string} selector1 - 需要缩放的内容区域选择器，默认 ".largeScreenContent"
 * @returns {object} 包含全屏状态、进入/退出全屏、缩放等方法
 */
export default function useFullscreen(selector = ".templateBigScreen2", selector1 = ".largeScreenContent") {
  // 全屏状态标记
  const screenFlag = ref(false);
  let targetElement = null;

  /**
   * 获取目标全屏元素
   * @returns {Element|null}
   */
  const getTargetElement = () => {
    if (!targetElement) {
      targetElement = document.querySelector(selector);
    }
    return targetElement;
  };

  /**
   * 进入全屏
   */
  const enterScreenClick = () => {
    const el = getTargetElement();
    if (!el) return;

    // 兼容不同浏览器的全屏 API
    if (el.requestFullscreen) {
      el.requestFullscreen();
    } else if (el.webkitRequestFullScreen) {
      el.webkitRequestFullScreen();
    } else if (el.mozRequestFullScreen) {
      el.mozRequestFullScreen();
    } else if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
    }

    screenFlag.value = true;
    triggerResize();
  };

  /**
   * 退出全屏
   */
  const exitFullscreenClick = () => {
    // 判断是否处于全屏状态
    if (!document.fullscreenElement && !document.webkitFullscreenElement && !document.mozFullScreenElement && !document.msFullscreenElement) return;

    // 兼容不同浏览器的退出全屏 API
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if (document.webkitCancelFullScreen) {
      document.webkitCancelFullScreen();
    } else if (document.mozCancelFullScreen) {
      document.mozCancelFullScreen();
    } else if (document.msExitFullscreen) {
      document.msExitFullscreen();
    }

    screenFlag.value = false;
    triggerResize();
  };

  /**
   * 页面自适应缩放
   */
  const resizeFun = () => {
    nextTick(() => {
      const ele = document.querySelector(selector);
      const content = document.querySelector(`${selector} ${selector1}`);
      if (!ele || !content) return;
      const scaleX = ele.clientWidth / 1920;
      const scaleY = ele.clientHeight / 1080;
      content.style.transform = `scale(${scaleX},${scaleY})`;
    });
  };

  /**
   * 延迟触发缩放（用于全屏切换后）
   */
  const triggerResize = () => {
    nextTick(() => {
      setTimeout(resizeFun, 100);
    });
  };
  /**
   * 监听全屏变化事件
   */
  const fullscreenChangeHandler = () => {
    console.log("窗口发生变化");
    // 更新全屏状态
    const isFullscreen = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement;
    screenFlag.value = !!isFullscreen;
    triggerResize();
  };

  // 添加事件监听
  if (typeof window !== "undefined") {
    document.addEventListener("fullscreenchange", fullscreenChangeHandler);
    document.addEventListener("webkitfullscreenchange", fullscreenChangeHandler);
    document.addEventListener("mozfullscreenchange", fullscreenChangeHandler);
    document.addEventListener("MSFullscreenChange", fullscreenChangeHandler);
  }

  return {
    screenFlag, // 全屏状态
    enterScreenClick, // 进入全屏方法
    exitFullscreenClick, // 退出全屏方法
    resizeFun, // 页面缩放方法
    triggerResize, // 延迟缩放方法
  };
}
